import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast, Toaster } from 'sonner';
import { 
  Users, Search, Filter, RefreshCcw, Mail, Phone, Building2,
  Calendar, MessageSquare, Brain, FileText, Unlock, ChevronDown,
  CheckCircle, Clock, XCircle, AlertCircle, MoreVertical, X,
  TrendingUp, Flame, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Lead Score Badge Component
const LeadScoreBadge = ({ score, factors = [] }) => {
  const getScoreColor = (score) => {
    if (score >= 70) return 'bg-green-100 text-green-700 border-green-300';
    if (score >= 50) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    if (score >= 30) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-gray-100 text-gray-600 border-gray-300';
  };

  const getScoreIcon = (score) => {
    if (score >= 70) return <Flame size={14} className="text-green-600" />;
    if (score >= 50) return <TrendingUp size={14} className="text-yellow-600" />;
    return <Star size={14} className="text-gray-500" />;
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return 'Hot';
    if (score >= 50) return 'Warm';
    if (score >= 30) return 'Cool';
    return 'Cold';
  };

  return (
    <div className="group relative">
      <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getScoreColor(score)}`}>
        {getScoreIcon(score)}
        <span className="ml-1">{score}</span>
        <span className="ml-1 hidden sm:inline">({getScoreLabel(score)})</span>
      </div>
      {factors && factors.length > 0 && (
        <div className="absolute z-50 hidden group-hover:block w-48 p-2 bg-white border border-border-line rounded-lg shadow-lg top-full left-0 mt-1">
          <div className="text-xs font-medium text-text-secondary mb-1">Score Factors:</div>
          {factors.slice(0, 4).map((f, i) => (
            <div key={i} className="flex items-center justify-between text-xs py-0.5">
              <span className="text-text-primary">{f.factor}</span>
              <span className={f.type === 'positive' ? 'text-green-600' : 'text-yellow-600'}>+{f.points}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminCRMPage = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [scoreFilter, setScoreFilter] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'score'
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadData();
  }, [sourceFilter, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      let url = `${API_URL}/api/admin/crm/leads?limit=100`;
      if (sourceFilter) url += `&source=${sourceFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const [leadsRes, statsRes] = await Promise.all([
        fetch(url, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/crm/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (leadsRes.ok) {
        const data = await leadsRes.json();
        setLeads(data);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (e) {
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (lead, newStatus) => {
    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/crm/leads/${lead.id}?source=${lead.source}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success('Status updated');
        loadData();
        if (selectedLead?.id === lead.id) {
          setSelectedLead({ ...selectedLead, status: newStatus });
        }
      } else {
        throw new Error('Failed to update');
      }
    } catch (e) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(
        `${API_URL}/api/admin/crm/leads/${selectedLead.id}/notes?source=${selectedLead.source}&note=${encodeURIComponent(newNote)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.ok) {
        toast.success('Note added');
        setNewNote('');
        // Refresh lead data
        const leadRes = await fetch(
          `${API_URL}/api/admin/crm/leads/${selectedLead.id}?source=${selectedLead.source}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (leadRes.ok) {
          const updatedLead = await leadRes.json();
          setSelectedLead({ ...selectedLead, ...updatedLead });
        }
      }
    } catch (e) {
      toast.error('Failed to add note');
    }
  };

  const filteredLeads = leads
    .filter(lead => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = (
          lead.name?.toLowerCase().includes(search) ||
          lead.email?.toLowerCase().includes(search) ||
          lead.company?.toLowerCase().includes(search) ||
          lead.company_name?.toLowerCase().includes(search)
        );
        if (!matchesSearch) return false;
      }
      
      // Score filter
      if (scoreFilter) {
        const score = lead.score || 0;
        if (scoreFilter === 'hot' && score < 70) return false;
        if (scoreFilter === 'warm' && (score < 50 || score >= 70)) return false;
        if (scoreFilter === 'cool' && (score < 30 || score >= 50)) return false;
        if (scoreFilter === 'cold' && score >= 30) return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'score') {
        return (b.score || 0) - (a.score || 0);
      }
      // Default: sort by date (newest first)
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

  const getSourceIcon = (source) => {
    const icons = {
      demo_request: FileText,
      contact_form: MessageSquare,
      ai_demo: Brain,
      booking: Calendar,
      feature_unlock: Unlock,
      pricing_unlock: Unlock
    };
    return icons[source] || Users;
  };

  const getSourceColor = (source) => {
    const colors = {
      demo_request: 'bg-tech-blue/10 text-tech-blue',
      contact_form: 'bg-heritage-orange/10 text-heritage-orange',
      ai_demo: 'bg-purple-500/10 text-purple-500',
      booking: 'bg-green-500/10 text-green-500',
      feature_unlock: 'bg-pink-500/10 text-pink-500',
      pricing_unlock: 'bg-care-teal/10 text-care-teal'
    };
    return colors[source] || 'bg-gray-100 text-gray-600';
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: { bg: 'bg-blue-100', text: 'text-blue-700', icon: AlertCircle },
      pending: { bg: 'bg-blue-100', text: 'text-blue-700', icon: AlertCircle },
      unread: { bg: 'bg-blue-100', text: 'text-blue-700', icon: AlertCircle },
      contacted: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      read: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      qualified: { bg: 'bg-purple-100', text: 'text-purple-700', icon: CheckCircle },
      confirmed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      won: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      lost: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle }
    };
    const badge = badges[status] || badges.new;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon size={12} className="mr-1" />
        {status || 'new'}
      </span>
    );
  };

  const statusOptions = ['new', 'contacted', 'qualified', 'proposal_sent', 'won', 'lost'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCcw className="animate-spin text-tech-blue" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">CRM</h1>
          <p className="text-text-secondary mt-1">Manage all leads, contacts, and bookings</p>
        </div>
        <Button onClick={loadData} variant="outline">
          <RefreshCcw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pipeline Stats */}
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          <Card className="border-border-line border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-text-primary">{stats.pipeline?.new || 0}</div>
              <div className="text-sm text-text-secondary">New Leads</div>
            </CardContent>
          </Card>
          <Card className="border-border-line border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-text-primary">{stats.pipeline?.contacted || 0}</div>
              <div className="text-sm text-text-secondary">Contacted</div>
            </CardContent>
          </Card>
          <Card className="border-border-line border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-text-primary">{stats.pipeline?.qualified || 0}</div>
              <div className="text-sm text-text-secondary">Qualified</div>
            </CardContent>
          </Card>
          <Card className="border-border-line border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-text-primary">{stats.pipeline?.won || 0}</div>
              <div className="text-sm text-text-secondary">Won</div>
            </CardContent>
          </Card>
          <Card className="border-border-line border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-text-primary">{stats.pipeline?.lost || 0}</div>
              <div className="text-sm text-text-secondary">Lost</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-border-line">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={18} />
                <Input
                  placeholder="Search by name, email, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
            >
              <option value="">All Sources</option>
              <option value="demo">Demo Requests</option>
              <option value="contact">Contact Forms</option>
              <option value="ai_demo">AI Demo</option>
              <option value="booking">Bookings</option>
              <option value="feature_unlock">Feature Unlocks</option>
              <option value="pricing_unlock">Pricing Unlocks</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>

            <select
              value={scoreFilter}
              onChange={(e) => setScoreFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
            >
              <option value="">All Scores</option>
              <option value="hot">🔥 Hot (70+)</option>
              <option value="warm">🌡️ Warm (50-69)</option>
              <option value="cool">❄️ Cool (30-49)</option>
              <option value="cold">🧊 Cold (&lt;30)</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
            >
              <option value="date">Sort by Date</option>
              <option value="score">Sort by Score</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="border-border-line">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-page-bg">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Lead</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Contact</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Score</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Source</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-text-secondary">
                      No leads found
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead, index) => {
                    const SourceIcon = getSourceIcon(lead.source);
                    return (
                      <tr 
                        key={`${lead.source}-${lead.id}-${index}`} 
                        className="border-b border-border-line hover:bg-page-bg cursor-pointer"
                        onClick={() => { setSelectedLead(lead); setShowDetailModal(true); }}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getSourceColor(lead.source)}`}>
                              <SourceIcon size={18} />
                            </div>
                            <div>
                              <div className="font-medium text-text-primary">{lead.name}</div>
                              <div className="text-sm text-text-secondary">{lead.company || lead.company_name || '-'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            <div className="flex items-center text-tech-blue">
                              <Mail size={14} className="mr-1" />
                              {lead.email}
                            </div>
                            {lead.phone && (
                              <div className="flex items-center text-text-secondary mt-1">
                                <Phone size={14} className="mr-1" />
                                {lead.phone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <LeadScoreBadge score={lead.score || 0} factors={lead.score_factors || []} />
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(lead.source)}`}>
                            <SourceIcon size={12} className="mr-1" />
                            {lead.source_label}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(lead.status)}
                        </td>
                        <td className="py-3 px-4 text-sm text-text-secondary">
                          {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setSelectedLead(lead); 
                              setShowDetailModal(true); 
                            }}
                          >
                            <MoreVertical size={16} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border-line">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getSourceColor(selectedLead.source)}`}>
                  {React.createElement(getSourceIcon(selectedLead.source), { size: 24 })}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-text-primary">{selectedLead.name}</h2>
                  <p className="text-sm text-text-secondary">{selectedLead.source_label}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-page-bg rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs text-text-secondary">Email</label>
                  <div className="flex items-center text-text-primary">
                    <Mail size={16} className="mr-2 text-tech-blue" />
                    {selectedLead.email}
                  </div>
                </div>
                {selectedLead.phone && (
                  <div className="space-y-1">
                    <label className="text-xs text-text-secondary">Phone</label>
                    <div className="flex items-center text-text-primary">
                      <Phone size={16} className="mr-2 text-tech-blue" />
                      {selectedLead.phone}
                    </div>
                  </div>
                )}
                {(selectedLead.company || selectedLead.company_name) && (
                  <div className="space-y-1">
                    <label className="text-xs text-text-secondary">Company</label>
                    <div className="flex items-center text-text-primary">
                      <Building2 size={16} className="mr-2 text-tech-blue" />
                      {selectedLead.company || selectedLead.company_name}
                    </div>
                  </div>
                )}
                {selectedLead.staffSize && (
                  <div className="space-y-1">
                    <label className="text-xs text-text-secondary">Staff Size</label>
                    <div className="text-text-primary">{selectedLead.staffSize}</div>
                  </div>
                )}
              </div>

              {/* Message/Subject if exists */}
              {(selectedLead.message || selectedLead.subject) && (
                <div className="space-y-1">
                  <label className="text-xs text-text-secondary">
                    {selectedLead.subject ? 'Subject' : 'Message'}
                  </label>
                  <div className="p-3 bg-page-bg rounded-lg text-text-primary">
                    {selectedLead.subject && <div className="font-medium mb-1">{selectedLead.subject}</div>}
                    {selectedLead.message}
                  </div>
                </div>
              )}

              {/* Booking Details if booking */}
              {selectedLead.source === 'booking' && (
                <div className="p-4 bg-tech-blue/5 rounded-lg border border-tech-blue/20">
                  <div className="flex items-center space-x-4">
                    <Calendar className="text-tech-blue" size={24} />
                    <div>
                      <div className="font-medium text-text-primary">
                        {selectedLead.date} at {selectedLead.time}
                      </div>
                      <div className="text-sm text-text-secondary">{selectedLead.duration || 30} minute call</div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Demo Interest if AI demo lead */}
              {selectedLead.feature_interest && (
                <div className="space-y-1">
                  <label className="text-xs text-text-secondary">Feature Interest</label>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-care-teal/10 text-care-teal capitalize">
                    <Brain size={14} className="mr-1" />
                    {selectedLead.feature_interest}
                  </span>
                </div>
              )}

              {/* Status Update */}
              <div className="space-y-2">
                <label className="text-xs text-text-secondary">Update Status</label>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map(status => (
                    <button
                      key={status}
                      onClick={() => updateLeadStatus(selectedLead, status)}
                      disabled={updatingStatus}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedLead.status === status 
                          ? 'bg-tech-blue text-white' 
                          : 'bg-page-bg text-text-secondary hover:bg-soft-sky'
                      }`}
                    >
                      {status.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Activity Notes */}
              <div className="space-y-3">
                <label className="text-xs text-text-secondary">Activity Notes</label>
                
                {/* Existing Notes */}
                {selectedLead.activity_notes?.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedLead.activity_notes.map((note, i) => (
                      <div key={i} className="p-3 bg-page-bg rounded-lg text-sm">
                        <p className="text-text-primary">{note.text}</p>
                        <p className="text-xs text-text-secondary mt-1">
                          {new Date(note.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add Note */}
                <div className="flex space-x-2">
                  <Input
                    placeholder="Add a note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addNote()}
                  />
                  <Button onClick={addNote} disabled={!newNote.trim()}>
                    Add
                  </Button>
                </div>
              </div>

              {/* Meta Info */}
              <div className="pt-4 border-t border-border-line text-xs text-text-secondary">
                <div>Created: {selectedLead.created_at ? new Date(selectedLead.created_at).toLocaleString() : '-'}</div>
                {selectedLead.updated_at && (
                  <div>Last Updated: {new Date(selectedLead.updated_at).toLocaleString()}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCRMPage;
